/** The daily reports table: GET /reports rows + MUI TablePagination (limit capped at
 *  100, a v1 quirk -- the server resets to 10 above that). Edit/Delete, Payout ratio
 *  and Source only render for reports:write (v1 parity: External never sees the
 *  ratio; Source joined it -- owner rulings 2026-09-01). report_type carries no
 *  provenance -- it is always the auditor-stamped shop_type, whichever way the row
 *  arrived; how it arrived is the separate Source column (sync | manual | import). */
import { Link as RouterLink } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { PageInfo, ReportRow } from '../../api/index.ts';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { shopDetailPath } from '../../routing/routes.ts';
import { palette } from '../../theme.ts';
import { payoutRatioPct, payoutRatioText } from '../dashboard/dashboardHelpers.ts';
import { SourceBadge } from './SourceBadge.tsx';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

/** v1 rule: red only once payout passes the day's net takings. */
function isOverspent(row: ReportRow): boolean {
  const ratio = payoutRatioPct(row.payout_minor, row.sales_minor, row.tax_minor);
  return ratio !== null && ratio > 100;
}

export interface ReportsTableProps {
  loading: boolean;
  /** True only for the very first load; drives the spinner row. Defaults to
   *  `loading` for callers that don't wire the reference-equality check. */
  firstLoad?: boolean;
  error: string | null;
  rows: ReportRow[];
  pageInfo: PageInfo;
  canWrite: boolean;
  /** Shop name becomes a /shops/:id link only with shops:read; plain text
   *  otherwise. Defaults false so a caller that forgets to pass it fails safe. */
  shopLinkEnabled?: boolean;
  /** Shop-scoped callers can remove constant columns to keep the ledger compact. */
  hideShopColumn?: boolean;
  hideTypeColumn?: boolean;
  /** Phone-width (<sm): hides Type/Payout/Tax so the table stops clipping.
   *  Page-driven prop (not an internal media query) to stay testable at either
   *  width. Defaults false so desktop/tablet render unchanged. */
  compact?: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onEdit: (row: ReportRow) => void;
  onDelete: (row: ReportRow) => void;
}

export function ReportsTable({
  loading,
  firstLoad = loading,
  error,
  rows,
  pageInfo,
  canWrite,
  shopLinkEnabled = false,
  hideShopColumn = false,
  hideTypeColumn = false,
  compact = false,
  onPageChange,
  onLimitChange,
  onEdit,
  onDelete,
}: Readonly<ReportsTableProps>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  if (error) {
    return (
      <Alert role="alert" severity="error">
        {error}
      </Alert>
    );
  }

  // Counts what renders: Type hides on hideTypeColumn OR compact; Source needs canWrite AND full width.
  const typeHidden = hideTypeColumn || compact;
  const colSpan =
    2 + // Date + Gross Sales
    (hideShopColumn ? 0 : 1) + // Shop
    (typeHidden ? 0 : 1) + // Type
    (compact ? 0 : 2) + // Payout + Tax
    (canWrite ? 2 : 0) + // Payout ratio + Actions
    (canWrite && !compact ? 1 : 0); // Source
  const refreshing = loading && !firstLoad;

  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {!hideShopColumn && <TableCell>Shop</TableCell>}
              {!typeHidden && <TableCell>Type</TableCell>}
              <TableCell>Date</TableCell>
              <TableCell align="right">Gross Sales</TableCell>
              {!compact && <TableCell align="right">Payout</TableCell>}
              {canWrite && <TableCell align="right">Payout ratio</TableCell>}
              {!compact && <TableCell align="right">Tax</TableCell>}
              {canWrite && !compact && <TableCell>Source</TableCell>}
              {canWrite && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody sx={refreshing ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
            {firstLoad && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                    }}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading reports...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!firstLoad && rows.length === 0 && <EmptyRow colSpan={colSpan} message="No reports for this period." />}
            {!firstLoad &&
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  {!hideShopColumn && (
                    <TableCell>
                      {shopLinkEnabled ? (
                        <Link component={RouterLink} to={shopDetailPath(row.shop_id)}>
                          {row.shop_name}
                        </Link>
                      ) : (
                        row.shop_name
                      )}
                    </TableCell>
                  )}
                  {!typeHidden && <TableCell sx={{ textTransform: 'capitalize' }}>{row.report_type}</TableCell>}
                  <TableCell>{row.submission_date}</TableCell>
                  <TableCell align="right">{format(row.sales_minor, row.currency_code)}</TableCell>
                  {!compact && <TableCell align="right">{format(row.payout_minor, row.currency_code)}</TableCell>}
                  {canWrite && (
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: isOverspent(row) ? palette.destructive : undefined,
                      }}
                    >
                      {payoutRatioText(row.payout_minor, row.sales_minor, row.tax_minor)}
                    </TableCell>
                  )}
                  {!compact && <TableCell align="right">{format(row.tax_minor, row.currency_code)}</TableCell>}
                  {canWrite && !compact && (
                    <TableCell>
                      <SourceBadge source={row.source} />
                    </TableCell>
                  )}
                  {canWrite && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={`Edit report ${row.id}`}
                        onClick={() => onEdit(row)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Delete report ${row.id}`}
                        onClick={() => onDelete(row)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pageInfo.total}
        page={Math.max(0, pageInfo.page - 1)}
        rowsPerPage={pageInfo.limit}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        onRowsPerPageChange={(e) => onLimitChange(Number(e.target.value))}
      />
    </Paper>
  );
}

export default ReportsTable;
